import React, { useState } from "react";
import { toast } from "sonner";
import { Download, Trash2 } from "lucide-react";
import { Input } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { fmtDateTime } from "@/lib/time";
import { auditAction, auditRole, auditTarget } from "@/lib/auditLabels";

type Row = { action: string; actor_name: string; actor_role: string; target_type: string; target_id: string; details?: Record<string, unknown>; created_at: string };
const cell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export const AuditPurge = ({ onPurged }: { onPurged: () => void }) => {
  const { t, lang } = usePrefs();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = !!from && !!to && from <= to;
  const fetchRows = async (): Promise<Row[]> => (await api.get("/admin/audit", { params: { from, to, limit: 20000 } })).data;

  const download = async () => {
    setBusy(true);
    try {
      const rows = await fetchRows();
      if (!rows.length) { toast.info(t("apg.none")); return; }
      const head = [t("apg.col.time"), t("apg.col.action"), t("apg.col.actor"), t("apg.col.role"), t("apg.col.target"), t("apg.col.id"), t("apg.col.details")];
      const body = rows.map((r) => [fmtDateTime(r.created_at, lang), auditAction(r.action, lang), r.actor_name, auditRole(r.actor_role, lang), auditTarget(r.target_type, lang), r.target_id, r.details ? JSON.stringify(r.details) : ""].map(cell).join(","));
      const blob = new Blob(["\ufeff" + [head.map(cell).join(","), ...body].join("\n")], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `audit_${from}_${to}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(errorText(e, t)); } finally { setBusy(false); }
  };

  const purge = async () => {
    setBusy(true);
    try {
      const n = (await fetchRows()).length;
      if (!n) { toast.info(t("apg.none")); return; }
      if (!window.confirm(t("apg.confirm").replace("{n}", String(n)).replace("{from}", from).replace("{to}", to))) return;
      const { data } = await api.delete("/admin/audit", { params: { from, to } });
      toast.success(t("apg.done").replace("{n}", String(data.deleted)));
      onPurged();
    } catch (e) { toast.error(errorText(e, t)); } finally { setBusy(false); }
  };

  const btn = "h-10 px-4 rounded-full text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50";
  return (
    <details data-testid="audit-purge" className="rounded-2xl border bg-card p-3 mb-4">
      <summary className="cursor-pointer text-sm font-bold">{t("apg.title")}</summary>
      <div className="pt-3 space-y-3">
        <p className="text-xs text-muted-foreground">{t("apg.note")}</p>
        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-xs font-bold space-y-1">{t("apg.from")}<Input type="date" value={from} max={to || undefined} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFrom(e.target.value)} data-testid="audit-purge-from" className="h-10 rounded-full" /></label>
          <label className="text-xs font-bold space-y-1">{t("apg.to")}<Input type="date" value={to} min={from || undefined} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTo(e.target.value)} data-testid="audit-purge-to" className="h-10 rounded-full" /></label>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={download} disabled={!ok || busy} data-testid="audit-purge-download" className={`${btn} border bg-background`}><Download className="h-4 w-4" />{t("apg.download")}</button>
          <button onClick={purge} disabled={!ok || busy} data-testid="audit-purge-delete" className={`${btn} bg-rose-600 text-white`}><Trash2 className="h-4 w-4" />{t("apg.delete")}</button>
        </div>
      </div>
    </details>
  );
};
